import type { Attempt, RoundConfig } from '@ranked-waiting/game-core';
import { clampCustomTargetRange } from '@ranked-waiting/game-core';
import { renderPageShell } from '../lib/page-shell';
import { renderRoundStage } from '../lib/round-stage';
import { formatDurationMs } from '../lib/format';
import { resultBadgeMarkup, resultTier } from '../lib/result-badge';
import { applyCachedTimerTheme } from '../lib/cosmetics';
import { applyCachedTimerFaceArt } from '../lib/catalog';
import type { RouteHandler } from '../lib/router';

const DEFAULT_TARGET_SECONDS = 10;
const DEFAULT_OFFSET_SECONDS = 0;
const MAX_OFFSET_SECONDS = 10;

/** Fixed target ± a random offset (0-10s) instead of the default fully-random 3-30s range — owner decision, 2026-08-11. */
function buildPracticeConfig(targetSeconds: number, offsetSeconds: number): RoundConfig {
  const clampedOffset = Math.min(Math.max(offsetSeconds, 0), MAX_OFFSET_SECONDS);
  const targetMs = Math.max(0, targetSeconds) * 1000;
  const offsetMs = clampedOffset * 1000;
  return clampCustomTargetRange(targetMs - offsetMs, targetMs + offsetMs);
}

export const renderPractice: RouteHandler = (container, navigate) => {
  const body = renderPageShell(container, navigate, 'play', {
    title: 'PRACTICE',
    subtitle: "Train your internal clock. No opponent, no pressure, just reps.",
    body: `
      <div class="practice-layout">
        <div>
          <form class="practice-settings" id="practice-settings">
            <label>
              Target (seconds)
              <input class="join-input" id="practice-target" type="number" min="0.5" max="120" step="0.1" value="${DEFAULT_TARGET_SECONDS}" />
            </label>
            <label>
              Offset (± seconds, 0–10)
              <input class="join-input" id="practice-offset" type="number" min="0" max="${MAX_OFFSET_SECONDS}" step="0.1" value="${DEFAULT_OFFSET_SECONDS}" />
            </label>
            <button class="button button--secondary" type="submit">Apply</button>
          </form>
          <div class="round-mount" id="round-mount"></div>
        </div>
        <aside class="practice-summary" id="practice-summary">
          <h2>Session</h2>
          <ul id="attempt-list" class="attempt-list"></ul>
          <p class="page-body__stub"><a href="#/challenge-10s">Try the 10 Second Challenge</a></p>
          <p class="page-body__stub"><a href="#/daily-challenge">Play today's Daily Challenge, free, no sign-in needed</a></p>
        </aside>
      </div>
    `,
  });

  const mount = body.querySelector<HTMLDivElement>('#round-mount')!;
  const attemptList = body.querySelector<HTMLUListElement>('#attempt-list')!;
  const settingsForm = body.querySelector<HTMLFormElement>('#practice-settings')!;
  const targetInput = body.querySelector<HTMLInputElement>('#practice-target')!;
  const offsetInput = body.querySelector<HTMLInputElement>('#practice-offset')!;
  const attempts: Attempt[] = [];
  let destroyRound: (() => void) | void;
  let config: RoundConfig = buildPracticeConfig(DEFAULT_TARGET_SECONDS, DEFAULT_OFFSET_SECONDS);

  applyCachedTimerTheme(mount);
  void applyCachedTimerFaceArt(mount);

  function renderSummary() {
    if (attempts.length === 0) {
      attemptList.innerHTML = `<li class="attempt-list__empty">Your attempts will appear here.</li>`;
      return;
    }
    const bestErrorMs = Math.min(...attempts.filter((a) => !a.isFalseStart).map((a) => a.errorMs));
    attemptList.innerHTML = attempts
      .slice()
      .reverse()
      .map((attempt, index) => {
        const isBest = !attempt.isFalseStart && attempt.errorMs === bestErrorMs;
        return `
          <li class="${attempt.isFalseStart ? 'is-false-start' : ''}${isBest ? ' is-best' : ''}" data-tier="${resultTier(attempt)}">
            <span>#${attempts.length - index}</span>
            <span>${formatDurationMs(attempt.targetMs)}s target</span>
            ${resultBadgeMarkup(attempt, { highlight: isBest })}
          </li>
        `;
      })
      .join('');
  }

  function startRound() {
    destroyRound = renderRoundStage(mount, {
      config,
      label: 'Practice attempt',
      onComplete: (attempt) => {
        attempts.push(attempt);
        renderSummary();
        if (destroyRound) destroyRound();
        startRound();
      },
    });
  }

  settingsForm.addEventListener('submit', (event) => {
    event.preventDefault();
    config = buildPracticeConfig(Number(targetInput.value) || DEFAULT_TARGET_SECONDS, Number(offsetInput.value) || 0);
    if (destroyRound) destroyRound();
    startRound();
  });

  renderSummary();
  startRound();

  return () => {
    if (destroyRound) destroyRound();
  };
};
